import { Injectable, inject, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ApiService } from './api.service';
import { Site } from 'portal-shared';
import { SiteSettingsService } from './site-settings.service';

@Injectable({
  providedIn: 'root',
})
export class SiteConfigService {
  private apiService = inject(ApiService);
  private siteSettingsService = inject(SiteSettingsService);

  private cachedSitePromise: Promise<Site> | null = null;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  async getCurrentSite(): Promise<Site> {
    if (!this.cachedSitePromise) {
      this.cachedSitePromise = this.apiService
        .get('/ari/sites/current')
        .then((response) => response as Site)
        .catch((error) => {
          this.cachedSitePromise = null;
          throw error;
        });
    }
    return this.cachedSitePromise;
  }

  async getSiteSettings() {
    return this.siteSettingsService.getSiteSettings();
  }

  async getCurrentSiteName(): Promise<string> {
    const [site, settings] = await Promise.all([
      this.getCurrentSite(),
      this.getSiteSettings(),
    ]);

    const hideNameInHeader = settings?.hideNameInHeader;
    return (
      (site?.name && !hideNameInHeader ? site.name + ' ' : '') +
      'ARConnect Admin'
    );
  }

  async getCurrentSiteLogo(): Promise<string | null> {
    const settings = await this.getSiteSettings();
    if (!settings?.logo) return null;

    const logo = settings.logo;

    // Use presigned URL from backend (works for both external and uploaded assets)
    return logo.url || null;
  }

  async applyThemeColorsFromSettings(): Promise<void> {
    const settings = await this.getSiteSettings();
    if (settings) {
      this.applyThemeColors(settings);
    }
  }

  // Helper to compute contrasting color based on luminance
  private getContrastingColor(hexColor: string): string {
    // Remove # if present
    const hex = hexColor.replace('#', '');

    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate luminance using weighted formula
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

    // Return white for dark colors, black for light colors
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }
  // Apply theme colors to CSS variables (viewer interface only)
  private applyThemeColors(settings: { primaryColor?: string }) {
    // Only apply colors to viewer interface, not admin; SSR-safe and supports multiple instances
    const viewerElements = this.document.querySelectorAll('.viewer-interface');
    if (!viewerElements.length) return;

    // Apply Kendo theme colors to all viewer interface instances
    viewerElements.forEach((viewerContainer) => {
      const viewerElement = viewerContainer as HTMLElement;

      if (settings.primaryColor) {
        viewerElement.style.setProperty(
          '--kendo-color-primary',
          settings.primaryColor,
        );

        // Compute contrasting color for text on primary background
        const contrastingColor = this.getContrastingColor(
          settings.primaryColor,
        );
        viewerElement.style.setProperty(
          '--kendo-color-on-primary',
          contrastingColor,
        );

        // Create lighter variations for hover/active states
        viewerElement.style.setProperty(
          '--kendo-color-primary-light',
          `color-mix(in srgb, ${settings.primaryColor} 80%, white 20%)`,
        );
        viewerElement.style.setProperty(
          '--kendo-color-primary-hover',
          `color-mix(in srgb, ${settings.primaryColor} 90%, white 10%)`,
        );

        // Also apply legacy accent color for backward compatibility
        viewerElement.style.setProperty(
          '--accent-color',
          settings.primaryColor,
        );
      }
    });
  }
}
